import React, { Suspense, useState } from 'react';

import { TextInput } from '@components/admin/Forms';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogTitle,
  ActionableDialogContent,
  ActionableDialogActions,
} from '@components/Dialog';
import { SubmittingOverlay } from '@components/Forms/SubmittingOverlay';
import { bookingInquiryTradeConditionStrings } from '@data/bookingInquiry';
import { allTradeConditions } from '@models/BookingInquiry';
import type { PreAlertDetails } from '@models/next/PreAlert';
import { PACKAGES, PORT_FROM, PORT_TO, VESSEL_NAMES } from '@models/PreAlert';
import { useCommonStyles } from '@styles';
import {
  createValidate,
  requiredStringField,
  stringField,
  stringNumberToThirdDecimalPlaceField,
} from '@utils/forms';
import { createStrings } from '@utils/local';
import { Formik, Form } from 'formik';
import * as t from 'io-ts';
import * as h from 'tyrann-io';

const CarrierAutoComplete = React.lazy(() => import('@components/Forms/CarrierAutoComplete'));

const strings = createStrings({
  ja: {
    before: '変更前',
    after: '変更後',
    reason: '変更理由',
    cancel: 'キャンセル',
    update: '変更',
  },
});

const valuesSchema = t.type({
  before: h.omittable(stringField(strings.before)),
  after: requiredStringField(strings.after),
  reason: requiredStringField(strings.reason),
});

type Values = t.TypeOf<typeof valuesSchema>;

const validate = createValidate(valuesSchema);

const valuesShipperMarkSchema = t.type({
  before: h.omittable(stringField(strings.before, 1, 1024)),
  after: requiredStringField(strings.after, 1, 1024),
  reason: requiredStringField(strings.reason),
});

const validateShipperMark = createValidate(valuesShipperMarkSchema);

const valuesCommoditySchema = t.type({
  before: h.omittable(stringField(strings.before, 1, 512)),
  after: requiredStringField(strings.after, 1, 512),
  reason: requiredStringField(strings.reason),
});

const validateCommodity = createValidate(valuesCommoditySchema);

const valuesGrossWeightSchema = t.type({
  before: stringNumberToThirdDecimalPlaceField(strings.before),
  after: stringNumberToThirdDecimalPlaceField(strings.after),
  reason: requiredStringField(strings.reason),
});

const validateGrossWeight = createValidate(valuesGrossWeightSchema);

const valuesMeasurementSchema = t.type({
  before: stringNumberToThirdDecimalPlaceField(strings.before),
  after: stringNumberToThirdDecimalPlaceField(strings.after),
  reason: requiredStringField(strings.reason),
});

const validateMeasurement = createValidate(valuesMeasurementSchema);

const selectValidate = (name?: keyof PreAlertDetails) => {
  switch (name) {
    case 'shipping_mark': {
      return validateShipperMark;
    }
    case 'gross_weight': {
      return validateGrossWeight;
    }
    case 'measurement': {
      return validateMeasurement;
    }
    case 'commodity': {
      return validateCommodity;
    }
    default: {
      return validate;
    }
  }
};

const WithVessel: React.FC<React.PropsWithChildren> = (props) => {
  const { children } = props;

  if (!React.isValidElement(children)) {
    return null;
  }

  return React.cloneElement<any, any>(children as any, {
    withAutoComplete: true,
    autoCompleteChoices: VESSEL_NAMES,
  });
};

const WithPortFrom: React.FC<React.PropsWithChildren> = (props) => {
  const { children } = props;

  if (!React.isValidElement(children)) {
    return null;
  }

  return React.cloneElement<any, any>(children as any, {
    withAutoComplete: true,
    autoCompleteChoices: PORT_FROM,
  });
};

const WithPortTo: React.FC<React.PropsWithChildren> = (props) => {
  const { children } = props;

  if (!React.isValidElement(children)) {
    return null;
  }

  return React.cloneElement<any, any>(children as any, {
    withAutoComplete: true,
    autoCompleteChoices: PORT_TO,
  });
};

const WithPackages: React.FC<React.PropsWithChildren> = (props) => {
  const { children } = props;

  if (!React.isValidElement(children)) {
    return null;
  }

  return React.cloneElement<any, any>(children as any, {
    withAutoComplete: true,
    autoCompleteChoices: PACKAGES,
  });
};

export const EditPreAlertFieldDialog: React.FC<EditPreAlertFieldDialogProps> = (props) => {
  const classes = useCommonStyles();
  const { name, before, onSubmit, open, fieldLabel, onClose } = props;

  const [textInputAnchor, setTextInputAnchor] = useState<HTMLInputElement>();

  if (!open) {
    return null;
  }

  const renderField = (type: 'before' | 'after') => {
    const baseProps = {
      name: type,
      label: strings[type],
      readOnly: type === 'before',
    };

    switch (name) {
      case 'vessel': {
        return (
          <WithVessel>
            <TextInput {...baseProps} />
          </WithVessel>
        );
      }
      case 'pod': {
        return (
          <WithPortFrom>
            <TextInput {...baseProps} />
          </WithPortFrom>
        );
      }
      case 'pol': {
        return (
          <WithPortTo>
            <TextInput {...baseProps} />
          </WithPortTo>
        );
      }
      case 'packages': {
        return (
          <WithPackages>
            <TextInput {...baseProps} />
          </WithPackages>
        );
      }
      case 'payments_terms': {
        return (
          <TextInput
            {...baseProps}
            choose
            options={allTradeConditions.map((c) => ({
              label: bookingInquiryTradeConditionStrings[c],
              value: c,
            }))}
          />
        );
      }
      case 'eta':
      case 'etd':
      case 'shipping_mark': {
        return <TextInput {...baseProps} multiline />;
      }
      case 'carrier':
        return (
          <>
            <TextInput
              {...baseProps}
              onClick={(e: any) => setTextInputAnchor(e.target)}
              onFocus={(e: any) => setTextInputAnchor(e.target)}
            />
            <Suspense fallback={null}>
              <CarrierAutoComplete
                name="after"
                anchor={textInputAnchor}
                onClose={() => setTextInputAnchor(undefined)}
              />
            </Suspense>
          </>
        );
      default: {
        return <TextInput {...baseProps} />;
      }
    }
  };

  return (
    <Formik
      validate={selectValidate(name)}
      initialValues={{ before, after: '', reason: '' }}
      onSubmit={async (values) => {
        await onSubmit(values);
        onClose();
      }}
    >
      {(formikProps) => {
        return (
          <Form>
            <SubmittingOverlay />
            <ActionableDialog open={open} onClose={onClose}>
              <ActionableDialogTitle>{fieldLabel}</ActionableDialogTitle>
              <ActionableDialogContent>
                <div className={classes.verticalSpace1}>
                  <div>{renderField('before')}</div>
                  <div>{renderField('after')}</div>
                  <div>
                    <TextInput name="reason" label={strings.reason} multiline />
                  </div>
                </div>
              </ActionableDialogContent>
              <ActionableDialogActions>
                <Button
                  color="printable"
                  text={strings.cancel}
                  size="small"
                  shape="round"
                  type="button"
                  onClick={() => {
                    formikProps.resetForm();
                    onClose();
                  }}
                />
                <Button
                  color="primary"
                  text={strings.update}
                  size="small"
                  shape="round"
                  type="submit"
                />
              </ActionableDialogActions>
            </ActionableDialog>
          </Form>
        );
      }}
    </Formik>
  );
};

export interface EditPreAlertFieldDialogProps {
  open: boolean;
  name?: keyof PreAlertDetails;
  before?: string;
  fieldLabel?: string;
  onClose: () => any;
  onSubmit: (values: Values) => any;
}
